import DownloadIcon from "@mui/icons-material/Download";
import {
  Box,
  CircularProgress,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  styled,
} from "@mui/material";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { CSVLink } from "react-csv";
import { useSelector } from "react-redux";

import { storeAnalyticsSelector } from "../../../../../redux/slices/store_analytics";

import OperationsRow, {
  buildMonthlyProjection,
  calculatePOFromProjection,
  getEffectiveShipments,
  getForecastData,
} from "./OperationsRow";
import { formatMonthLabelWithRestOf } from "./restOfMonthUtils";

const STATUS_LABEL = { ok: "OK", low: "LOW", oos: "OOS" };

// Single-cell, scannable summary per month for the CSV export. Shape:
//   "OK  | 18,147 (+0) -4,600 = 13,547"
//   "OOS | 1 (+0) -75 = -74"
const formatMonthCell = (m) => {
  if (!m) return "";
  const status = STATUS_LABEL[m.status] || m.status;
  const start = m.start.toLocaleString();
  const incoming = m.incoming.toLocaleString();
  const forecast = m.forecast.toLocaleString();
  const end = m.end.toLocaleString();
  const restNote =
    m.isRestOfMonth && m.forecastFullMonth != null
      ? ` [rest of month; full=${m.forecastFullMonth.toLocaleString()}]`
      : "";
  return `${status} | ${start} (+${incoming}) -${forecast} = ${end}${restNote}`;
};

const computeUnscheduledByType = (row) => {
  const shipments = getEffectiveShipments(row);
  const scheduledByType = shipments.reduce((acc, s) => {
    if (s && s.eta && s.quantity) {
      acc[s.type] = (acc[s.type] || 0) + (Number(s.quantity) || 0);
    }
    return acc;
  }, {});
  const totalsByType = {
    on_the_way: row.on_the_way_inventory || 0,
    in_work: row.in_work_inventory || 0,
    awd_inbound: row.awd_inbound || 0,
  };
  return Object.fromEntries(
    Object.entries(totalsByType).map(([type, total]) => [
      type,
      Math.max(total - (scheduledByType[type] || 0), 0),
    ])
  );
};

// CSV export functions
const getCSVData = (
  groupedData,
  predictedUnits,
  pastUnitsSales,
  sellerForecastReviews,
  monthKeys
) => {
  return groupedData.map((row, index) => {
    const {
      original,
      override,
      hasOverride,
      predictedItem,
      sellerForecastItem,
    } = getForecastData(row.child_asin, predictedUnits, sellerForecastReviews);

    const projection = buildMonthlyProjection(
      row,
      predictedItem,
      sellerForecastItem
    );
    const projectionByMonth = projection.reduce((acc, m) => {
      acc[m.monthKey] = m;
      return acc;
    }, {});

    const unscheduled = computeUnscheduledByType(row);
    const unscheduledTotal =
      unscheduled.on_the_way + unscheduled.in_work + unscheduled.awd_inbound;

    const excludedFromPOMonths = projection
      .filter((m) => m.excludedFromPO)
      .map((m) => `${m.label} (${Math.max(-m.end, 0)})`)
      .join("; ");

    const base = {
      "#": index + 1,
      Store: row.store,
      "Parent ASIN": row.parent_asin,
      "Child ASIN": row.child_asin,
      SKU: row.sku,
      COGS: row.cogs != null ? row.cogs : "",
      DoS: row.dos != null ? Math.round(row.dos) : "",
      PO: calculatePOFromProjection(projection),
      "PO Excluded (OOS within lead time)": excludedFromPOMonths,
      "All Inventory": row.all_inventory,
      "FBA Inventory": row.fba_inventory,
      "AWD Inventory": row.awd_inventory,
      "AWD Outbound": row.awd_outbound,
      "AWD Inbound": row.awd_inbound,
      "3PL Inventory": row["3pl_inventory"],
      "In Work Inventory": row.in_work_inventory,
      "On The Way Inventory": row.on_the_way_inventory,
      "Unscheduled Incoming (Total)": unscheduledTotal,
      "Unscheduled On The Way": unscheduled.on_the_way,
      "Unscheduled In Work": unscheduled.in_work,
      "Unscheduled AWD Inbound": unscheduled.awd_inbound,
      "Sales Forecast Next 7 Months (day to day)": hasOverride
        ? override
        : original,
      "Last 3 Months":
        pastUnitsSales.find(
          ({ parent_asin, child_asin, sku }) =>
            parent_asin === row.parent_asin &&
            child_asin === row.child_asin &&
            sku === row.sku
        )?.recent_units_3m || 0,
      "Last 3 Months LY":
        pastUnitsSales.find(
          ({ parent_asin, child_asin, sku }) =>
            parent_asin === row.parent_asin &&
            child_asin === row.child_asin &&
            sku === row.sku
        )?.last_year_units_3m || 0,
      "Next 3 Months LY":
        pastUnitsSales.find(
          ({ parent_asin, child_asin, sku }) =>
            parent_asin === row.parent_asin &&
            child_asin === row.child_asin &&
            sku === row.sku
        )?.last_year_units_next_3m || 0,
      "Next 7 Months LY":
        pastUnitsSales.find(
          ({ parent_asin, child_asin, sku }) =>
            parent_asin === row.parent_asin &&
            child_asin === row.child_asin &&
            sku === row.sku
        )?.last_year_units_next_7m || 0,
    };

    monthKeys.forEach((monthKey) => {
      const label = formatMonthLabelWithRestOf(monthKey);
      base[label] = formatMonthCell(projectionByMonth[monthKey]);
    });

    return base;
  });
};

const getCSVHeaders = (monthKeys) => {
  const headers = [
    { label: "#", key: "#" },
    { label: "Store", key: "Store" },
    { label: "Parent ASIN", key: "Parent ASIN" },
    { label: "Child ASIN", key: "Child ASIN" },
    { label: "SKU", key: "SKU" },
    { label: "COGS", key: "COGS" },
    { label: "DoS", key: "DoS" },
    { label: "PO", key: "PO" },
    {
      label: "PO Excluded (OOS within lead time)",
      key: "PO Excluded (OOS within lead time)",
    },
    { label: "All Inventory", key: "All Inventory" },
    { label: "FBA", key: "FBA Inventory" },
    { label: "AWD", key: "AWD Inventory" },
    { label: "AWD Outbound", key: "AWD Outbound" },
    { label: "AWD Inbound", key: "AWD Inbound" },
    { label: "3PL", key: "3PL Inventory" },
    { label: "In Work", key: "In Work Inventory" },
    { label: "On The Way", key: "On The Way Inventory" },
    {
      label: "Unscheduled Incoming (Total)",
      key: "Unscheduled Incoming (Total)",
    },
    { label: "Unscheduled On The Way", key: "Unscheduled On The Way" },
    { label: "Unscheduled In Work", key: "Unscheduled In Work" },
    { label: "Unscheduled AWD Inbound", key: "Unscheduled AWD Inbound" },
    {
      label: "Sales Forecast Next 7 Months (day to day)",
      key: "Sales Forecast Next 7 Months (day to day)",
    },
    { label: "Last 3 Months", key: "Last 3 Months" },
    { label: "Last 3 Months LY", key: "Last 3 Months LY" },
    { label: "Next 3 Months LY", key: "Next 3 Months LY" },
    { label: "Next 7 Months LY", key: "Next 7 Months LY" },
  ];

  monthKeys.forEach((monthKey) => {
    const label = formatMonthLabelWithRestOf(monthKey);
    headers.push({ label, key: label });
  });

  return headers;
};

const StyledHeaderCell = styled(TableCell)(() => ({
  fontWeight: 600,
  fontSize: "12px",
  backgroundColor: "#f8fafc",
  color: "#475569",
  position: "sticky",
  top: 62,
  zIndex: 1,
  textTransform: "uppercase",
  letterSpacing: "0.5px",
  padding: "16px 12px",
  borderBottom: "2px solid #e2e8f0",
  boxShadow: "0 1px 2px rgba(0, 0, 0, 0.03)",
}));

const Operations = ({ store }) => {
  const {
    activeInventory,
    activeInventoryLoading,
    predictedUnits,
    predictedUnitsLoading,
    pastUnitsSales,
    pastUnitsSalesLoading,
    sellerForecastReviews,
    // sellerForecastReviewsLoading,
  } = useSelector(storeAnalyticsSelector);

  const [sortConfig, setSortConfig] = useState({
    column: "forecast",
    direction: "desc",
  });

  // Stretch the table to the bottom of the viewport. The chrome above it
  // (store selector, tabs, sub-tabs) varies by page state, so measure where
  // the container actually starts instead of hardcoding an offset. 24px is
  // the TabPanel bottom gutter.
  const containerRef = useRef(null);
  const [containerTop, setContainerTop] = useState(null);
  useEffect(() => {
    if (containerRef.current) {
      const rect = containerRef.current.getBoundingClientRect();
      setContainerTop(rect.top + window.scrollY);
    }
  }, []);

  const handleSort = (column) => {
    setSortConfig((prev) => ({
      column,
      direction:
        prev.column === column && prev.direction === "desc" ? "asc" : "desc",
    }));
  };

  const groupedData = useMemo(() => {
    if (!activeInventory) return [];

    // Normalize to array regardless of whether the API returns an array or object
    const predictedUnitsList = Array.isArray(predictedUnits)
      ? predictedUnits
      : predictedUnits && typeof predictedUnits === "object"
      ? Object.values(predictedUnits)
      : [];

    const activeRows = Object.values(activeInventory);

    // Add synthetic rows for ASINs that have forecasts but no active inventory
    // (e.g. out-of-stock products filtered out by the backend).
    const coveredChildAsins = new Set(activeRows.map((r) => r.child_asin));
    const syntheticRows = [];
    predictedUnitsList.forEach((unit) => {
      const childAsin = unit.child_asin || unit.asin;
      if (!childAsin || coveredChildAsins.has(childAsin)) return;

      const pastEntries = (pastUnitsSales || []).filter(
        (s) => s.child_asin === childAsin && !s.sku?.startsWith("amzn.gr")
      );
      if (!pastEntries.length) return;

      pastEntries.forEach((pastData) => {
        syntheticRows.push({
          store,
          parent_asin: pastData.parent_asin || "",
          child_asin: childAsin,
          sku: pastData.sku || childAsin,
          fba_inventory: 0,
          awd_inventory: 0,
          awd_inbound: 0,
          awd_outbound: 0,
          in_work_inventory: 0,
          on_the_way_inventory: 0,
          "3pl_inventory": 0,
          all_inventory: 0,
          cogs: null,
          dos: null,
          incoming_shipments: [],
          noActiveInventory: true,
        });
      });
      coveredChildAsins.add(childAsin);
    });

    const rows = [...activeRows, ...syntheticRows];

    const getSortValue = (row) => {
      switch (sortConfig.column) {
        case "store":
          return row.store;
        case "parent_asin":
          return row.parent_asin;
        case "child_asin":
          return row.child_asin;
        case "sku":
          return row.sku;
        case "cogs":
          return row.cogs != null ? row.cogs : -1;
        case "dos":
          return row.dos != null ? row.dos : -1;
        case "po": {
          const { predictedItem, sellerForecastItem } = getForecastData(
            row.child_asin,
            predictedUnitsList,
            sellerForecastReviews || []
          );
          const projection = buildMonthlyProjection(
            row,
            predictedItem,
            sellerForecastItem
          );
          return calculatePOFromProjection(projection);
        }
        case "fba_awd_3pl":
          return row.fba_inventory + row.awd_inventory + row["3pl_inventory"];
        case "all_inventory":
          return row.all_inventory;
        case "forecast": {
          const { original, override, hasOverride } = getForecastData(
            row.child_asin,
            predictedUnitsList,
            sellerForecastReviews || []
          );
          return hasOverride ? override : original;
        }
        case "last3m": {
          const found = (pastUnitsSales || []).find(
            ({ parent_asin, child_asin, sku }) =>
              parent_asin === row.parent_asin &&
              child_asin === row.child_asin &&
              sku === row.sku
          );
          return found?.recent_units_3m || 0;
        }
        case "last3m_ly": {
          const found = (pastUnitsSales || []).find(
            ({ parent_asin, child_asin, sku }) =>
              parent_asin === row.parent_asin &&
              child_asin === row.child_asin &&
              sku === row.sku
          );
          return found?.last_year_units_3m || 0;
        }
        case "next3m_ly": {
          const found = (pastUnitsSales || []).find(
            ({ parent_asin, child_asin, sku }) =>
              parent_asin === row.parent_asin &&
              child_asin === row.child_asin &&
              sku === row.sku
          );
          return found?.last_year_units_next_3m || 0;
        }
        case "next7m_ly": {
          const found = (pastUnitsSales || []).find(
            ({ parent_asin, child_asin, sku }) =>
              parent_asin === row.parent_asin &&
              child_asin === row.child_asin &&
              sku === row.sku
          );
          return found?.last_year_units_next_7m || 0;
        }
        default:
          return 0;
      }
    };

    const sortedRows = [...rows].sort((rowA, rowB) => {
      const valA = getSortValue(rowA);
      const valB = getSortValue(rowB);
      const dir = sortConfig.direction === "asc" ? 1 : -1;
      if (typeof valA === "string") return valA.localeCompare(valB) * dir;
      return (valA - valB) * dir;
    });

    // Group sorted rows for striping
    const groups = {};
    sortedRows.forEach((row, index) => {
      const groupKey = `${row.store}-${row.parent_asin}-${row.child_asin}`;
      if (!groups[groupKey]) {
        groups[groupKey] = { firstIndex: index, rows: [] };
      }
      groups[groupKey].rows.push({ ...row, sortedIndex: index });
    });

    // Apply striping based on logical groups (consecutive same group)
    let currentGroupKey = null;
    let logicalRowIndex = 0;

    return sortedRows.map((row, index) => {
      const groupKey = `${row.store}-${row.parent_asin}-${row.child_asin}`;
      const group = groups[groupKey];
      const indexInGroup = group.rows.findIndex((r) => r.sortedIndex === index);
      const isLastInGroup = indexInGroup === group.rows.length - 1;

      if (groupKey !== currentGroupKey) {
        currentGroupKey = groupKey;
        logicalRowIndex++;
      }

      return {
        ...row,
        logicalRowIndex,
        isEvenLogicalRow: logicalRowIndex % 2 === 0,
        isLastInGroup,
        groupSize: group.rows.length,
      };
    });
  }, [
    activeInventory,
    predictedUnits,
    pastUnitsSales,
    sellerForecastReviews,
    sortConfig,
    store,
  ]);

  // Union of forecast month keys across all rows, sorted ascending. Used as
  // the column set for the per-month CSV breakdown so different ASINs with
  // slightly different forecast horizons all align in the same spreadsheet.
  const allMonthKeys = useMemo(() => {
    const set = new Set();
    const list = Array.isArray(predictedUnits)
      ? predictedUnits
      : predictedUnits && typeof predictedUnits === "object"
      ? Object.values(predictedUnits)
      : [];
    list.forEach((p) => {
      if (p && p.predictions) {
        Object.keys(p.predictions).forEach((k) => set.add(k));
      }
    });
    return Array.from(set).sort();
  }, [predictedUnits]);

  return (
    <TableContainer
      ref={containerRef}
      component={Paper}
      sx={{
        height:
          containerTop != null
            ? `calc(100vh - ${containerTop + 24}px)`
            : "calc(100vh - 200px)",
        minHeight: "320px",
        borderRadius: "12px",
        boxShadow: "0 2px 8px rgba(0, 0, 0, 0.08)",
        border: "1px solid #e2e8f0",
        overflow: "auto",
      }}
    >
      <Table stickyHeader sx={{ tableLayout: "fixed", width: "100%" }}>
        <colgroup>
          <col style={{ width: "36px" }} />
          <col span={15} />
        </colgroup>
        <TableHead>
          <TableRow>
            <TableCell
              colSpan={16}
              sx={{
                textAlign: "right",
                backgroundColor: "#f8fafc",
                position: "sticky",
                top: 0,
                zIndex: 2,
                padding: "12px 16px",
                borderBottom: "2px solid #e2e8f0",
              }}
            >
              <CSVLink
                data={getCSVData(
                  groupedData,
                  predictedUnits || [],
                  pastUnitsSales || [],
                  sellerForecastReviews || [],
                  allMonthKeys
                )}
                filename={`${store}_store_products_report.csv`}
                headers={getCSVHeaders(allMonthKeys)}
                onClick={
                  activeInventoryLoading ||
                  predictedUnitsLoading ||
                  pastUnitsSalesLoading
                    ? (e) => e.preventDefault()
                    : undefined
                }
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  textDecoration: "none",
                  fontSize: "13px",
                  fontWeight: 600,
                  padding: "8px 16px",
                  borderRadius: "8px",
                  backgroundColor: "#10b981",
                  color: "#ffffff",
                  transition: "all 0.2s ease-in-out",
                  boxShadow: "0 2px 4px rgba(16, 185, 129, 0.3)",
                  opacity:
                    activeInventoryLoading ||
                    predictedUnitsLoading ||
                    pastUnitsSalesLoading
                      ? 0.5
                      : 1,
                  cursor:
                    activeInventoryLoading ||
                    predictedUnitsLoading ||
                    pastUnitsSalesLoading
                      ? "not-allowed"
                      : "pointer",
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = "#059669";
                  e.currentTarget.style.transform = "translateY(-1px)";
                  e.currentTarget.style.boxShadow =
                    "0 4px 8px rgba(16, 185, 129, 0.4)";
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = "#10b981";
                  e.currentTarget.style.transform = "translateY(0)";
                  e.currentTarget.style.boxShadow =
                    "0 2px 4px rgba(16, 185, 129, 0.3)";
                }}
              >
                <DownloadIcon
                  style={{ marginRight: "8px", fontSize: "18px" }}
                />
                <span>Download as CSV</span>
              </CSVLink>
            </TableCell>
          </TableRow>
          <TableRow>
            <StyledHeaderCell
              sx={{ textAlign: "center", width: "36px", paddingX: 0 }}
            />
            <StyledHeaderCell sx={{ textAlign: "center", width: "60px" }}>
              #
            </StyledHeaderCell>
            {[
              { label: "Store", column: "store" },
              { label: "Parent ASIN", column: "parent_asin" },
              { label: "Child ASIN", column: "child_asin" },
              { label: "SKU", column: "sku" },
            ].map(({ label, column }) => (
              <StyledHeaderCell key={column}>
                <TableSortLabel
                  active={sortConfig.column === column}
                  direction={
                    sortConfig.column === column ? sortConfig.direction : "asc"
                  }
                  onClick={() => handleSort(column)}
                >
                  {label}
                </TableSortLabel>
              </StyledHeaderCell>
            ))}
            {[
              { label: "COGS", column: "cogs", width: "80px" },
              { label: "DoS", column: "dos", width: "70px" },
              { label: "PO", column: "po", width: "70px" },
              { label: "FBA + AWD + 3PL", column: "fba_awd_3pl" },
              { label: "All Inventory", column: "all_inventory" },
              {
                label: "Sales Forecast Next 7 Months (day to day)",
                column: "forecast",
              },
              { label: "Last 3 Months", column: "last3m" },
              { label: "Last 3 Months LY", column: "last3m_ly" },
              { label: "Next 3 Months LY", column: "next3m_ly" },
              { label: "Next 7 Months LY", column: "next7m_ly" },
            ].map(({ label, column, width }) => (
              <StyledHeaderCell
                key={column}
                sx={{ textAlign: "center", ...(width ? { width } : {}) }}
              >
                <TableSortLabel
                  active={sortConfig.column === column}
                  direction={
                    sortConfig.column === column ? sortConfig.direction : "asc"
                  }
                  onClick={() => handleSort(column)}
                  sx={{
                    "& .MuiTableSortLabel-root": {
                      flexDirection: "row-reverse",
                    },
                  }}
                >
                  {label}
                </TableSortLabel>
              </StyledHeaderCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {(activeInventoryLoading ||
            predictedUnitsLoading ||
            pastUnitsSalesLoading) && (
            <TableRow>
              <TableCell colSpan={16}>
                <Box
                  display="flex"
                  flexDirection="column"
                  justifyContent="center"
                  alignItems="center"
                  gap={2}
                  py={8}
                >
                  <CircularProgress
                    size={48}
                    thickness={4}
                    sx={{ color: "#3b82f6" }}
                  />
                  <Box
                    sx={{
                      color: "#64748b",
                      fontWeight: 500,
                      fontSize: "14px",
                    }}
                  >
                    Loading operations data...
                  </Box>
                </Box>
              </TableCell>
            </TableRow>
          )}
          {!activeInventoryLoading &&
            !predictedUnitsLoading &&
            !pastUnitsSalesLoading &&
            activeInventory &&
            predictedUnits &&
            groupedData.map((row, index) => (
              <OperationsRow
                key={`${row.store}-${row.parent_asin}-${row.child_asin}-${row.sku}`}
                row={row}
                index={index}
                predictedUnits={predictedUnits}
                pastUnitsSales={pastUnitsSales}
                sellerForecastReviews={sellerForecastReviews}
              />
            ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

export default Operations;
